import {
  isEmailConnector,
  getEmailConnectorConfig,
  upsertConnector,
  type BeehiivConnectorConfig,
  type BentoConnectorConfig,
  type ContentPublishingConnector,
  type GhostConnectorConfig,
  type SubstackUnofficialConnectorConfig,
  type WebhookConnectorConfig,
  type WordPressConnectorConfig,
  type WordPressSelfHostedConnectorConfig,
} from "@/lib/composerCore";
import { emptyEditor, type EditorState } from "@/components/composer/connectorManagerHelpers";
import {
  buildBeehiivUpsert,
  buildButtondownUpsert,
  buildBentoUpsert,
  buildEmailUpsert,
  buildGhostUpsert,
  buildKitUpsert,
  buildSubstackUnofficialUpsert,
  buildWebhookUpsert,
  buildWordPressUpsert,
  buildWordPressSelfHostedUpsert,
  type HandlerResult,
} from "./connectorEditorHandlers";

/**
 * Build an EditorState that opens the editor pre-filled for an existing connector.
 * Pure function — no side effects.
 */
export const connectorToEditorState = (connector: ContentPublishingConnector): EditorState => {
  if (isEmailConnector(connector)) {
    const cfg = getEmailConnectorConfig(connector);
    return {
      ...emptyEditor, open: true, connector, connectorType: "email",
      displayName: connector.display_name,
      fromName: cfg.from_name ?? "", replyTo: cfg.reply_to ?? "",
      recipients: cfg.recipients, subjectTemplate: cfg.subject_template ?? "{{title}}",
    };
  }
  if (connector.connector_type === "kit") {
    return {
      ...emptyEditor, open: true, connector, connectorType: "kit",
      displayName: connector.display_name, secretValue: "", clearSecret: false,
    };
  }
  if (connector.connector_type === "bento") {
    const cfg = (connector.config ?? {}) as Partial<BentoConnectorConfig>;
    return {
      ...emptyEditor, open: true, connector, connectorType: "bento",
      displayName: connector.display_name,
      bentoSiteUuid: cfg.site_uuid ?? "",
      bentoFromName: cfg.from_name ?? "",
      bentoFromEmail: cfg.from_email ?? "",
      bentoPublishableKey: "", secretValue: "", clearSecret: false,
    };
  }
  if (connector.connector_type === "wordpress") {
    const cfg = (connector.config ?? {}) as Partial<WordPressConnectorConfig>;
    return {
      ...emptyEditor, open: true, connector, connectorType: "wordpress",
      displayName: connector.display_name, wpSite: cfg.site ?? "",
    };
  }
  if (connector.connector_type === "wordpress_self_hosted") {
    const cfg = (connector.config ?? {}) as Partial<WordPressSelfHostedConnectorConfig>;
    return {
      ...emptyEditor, open: true, connector, connectorType: "wordpress_self_hosted",
      displayName: connector.display_name,
      wpShSiteUrl: cfg.site_url ?? "", wpShUsername: cfg.username ?? "",
      secretValue: "", clearSecret: false,
    };
  }
  // bluesky/mastodon connectors retired 2026-07-03.
  if (connector.connector_type === "ghost") {
    const cfg = (connector.config ?? {}) as Partial<GhostConnectorConfig>;
    return {
      ...emptyEditor, open: true, connector, connectorType: "ghost",
      displayName: connector.display_name,
      ghostAdminUrl: cfg.admin_url ?? "",
      secretValue: "", clearSecret: false,
    };
  }
  if (connector.connector_type === "substack_unofficial") {
    const cfg = (connector.config ?? {}) as Partial<SubstackUnofficialConnectorConfig>;
    return {
      ...emptyEditor, open: true, connector, connectorType: "substack_unofficial",
      displayName: connector.display_name,
      substackPublicationUrl: cfg.publication_url ?? "",
      secretValue: "", clearSecret: false,
    };
  }
  if (connector.connector_type === "beehiiv") {
    const cfg = (connector.config ?? {}) as Partial<BeehiivConnectorConfig>;
    return {
      ...emptyEditor, open: true, connector, connectorType: "beehiiv",
      displayName: connector.display_name,
      beehiivPublicationId: cfg.publication_id ?? "",
      secretValue: "", clearSecret: false,
    };
  }
  if (connector.connector_type === "buttondown") {
    return {
      ...emptyEditor, open: true, connector, connectorType: "buttondown",
      displayName: connector.display_name,
      secretValue: "", clearSecret: false,
    };
  }
  const cfg = (connector.config ?? {}) as WebhookConnectorConfig;
  return {
    ...emptyEditor, open: true, connector, connectorType: "webhook",
    displayName: connector.display_name,
    targetUrl: cfg.target_url ?? "", authHeaderName: cfg.auth_header_name ?? "",
    secretValue: "", clearSecret: false,
  };
};

export type SaveConnectorResult =
  | { ok: true; saved: ContentPublishingConnector; successMessage: string }
  | { ok: false; errorMessage: string };

const buildHandlerResult = (editor: EditorState, wpLinked: boolean | null): HandlerResult => {
  switch (editor.connectorType) {
    case "webhook": return buildWebhookUpsert(editor);
    case "kit": return buildKitUpsert(editor);
    case "bento": return buildBentoUpsert(editor);
    case "wordpress": return buildWordPressUpsert(editor, wpLinked);
    case "wordpress_self_hosted": return buildWordPressSelfHostedUpsert(editor);
    case "ghost": return buildGhostUpsert(editor);
    case "substack_unofficial": return buildSubstackUnofficialUpsert(editor);
    case "beehiiv": return buildBeehiivUpsert(editor);
    case "buttondown": return buildButtondownUpsert(editor);
    default: return buildEmailUpsert(editor); // "email"
  }
};

/**
 * Validate the editor state and persist the connector. Returns either a saved
 * connector with a success message, or a structured error message. The caller
 * is responsible for toasts and state updates.
 */
export const saveConnectorFromEditor = async ({
  workspaceId,
  editor,
  wpLinked,
}: {
  workspaceId: string;
  editor: EditorState;
  wpLinked: boolean | null;
}): Promise<SaveConnectorResult> => {
  if (!editor.displayName.trim()) {
    return { ok: false, errorMessage: "Display name is required" };
  }
  const result = buildHandlerResult(editor, wpLinked);
  if (result.ok === false) return { ok: false, errorMessage: result.errorMessage };
  try {
    const saved = await upsertConnector(workspaceId, result.upsert);
    return {
      ok: true,
      saved,
      successMessage: editor.connector ? result.updateMessage : result.createMessage,
    };
  } catch (err) {
    return { ok: false, errorMessage: (err as Error).message ?? "Could not save connector" };
  }
};
